import { FormFieldControl, Input } from "@components";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Select from "react-select";
import ModalBase from "@/components/ui/modal/modal-base";
import { useToast } from "@/providers/toast-provider";
import { handleEditDriverError } from "../errors/edit-driver.errors";
import {
	useDriverById,
	usePresignedUrl,
	useUpdateDriver,
	useVehicles,
} from "../hooks";
import { type EditDriverFormValues, editDriverSchema } from "../schema";
import type { AnnotatedFileWithInput, EditDriverModalProps } from "../types";
import { formatCpfMasked, uploadCnhFile } from "../utils";
import { FileInput } from "./FileInput";

const EditDriverModal = ({
	isOpen,
	driverId,
	onClose,
}: EditDriverModalProps) => {
	const { showToast } = useToast();
	const [cnhFile, setCnhFile] = useState<AnnotatedFileWithInput | null>(null);
	const [removedCnhPhoto, setRemovedCnhPhoto] = useState(false);
	const [loading, setLoading] = useState(false);

	const { mutateAsync } = useUpdateDriver();
	const { mutateAsync: getPresignedUrl } = usePresignedUrl();
	const { data: vehicles, isLoading: vehiclesLoading } = useVehicles(isOpen);
	const { data } = useDriverById(driverId ?? 0);

	const {
		register,
		handleSubmit,
		control,
		reset,
		setError,
		formState: { errors },
	} = useForm<EditDriverFormValues>({
		resolver: zodResolver(editDriverSchema),
		defaultValues: {
			name: "",
			cpf: "",
			email: "",
			vehicleId: "",
			function: "",
			cnh: "",
			cnhExpiration: "",
			categoryCnh: [],
			photoCnh: undefined,
		},
	});

	useEffect(() => {
		if (data) {
			const normalizedCategories = data.categoryCnh
				? data.categoryCnh.replace(/,/g, "").split("")
				: [];

			reset({
				name: data.name ?? "",
				cpf: data.cpf ?? "",
				email: data.email ?? "",
				function: data.function ?? "",
				cnh: data.cnh ?? "",
				cnhExpiration: data.cnhExpiration ?? "",
				categoryCnh: normalizedCategories,
				vehicleId: data?.vehicle?.id?.toString() ?? "",
			});
		}
	}, [data, reset]);

	const onSubmit = async (values: EditDriverFormValues) => {
		if (!data) {
			return;
		}

		if (
			values.cnh &&
			!values.photoCnh &&
			!cnhFile &&
			(!data.photo || removedCnhPhoto)
		) {
			setError("photoCnh", {
				type: "manual",
				message: "A foto da CNH é obrigatória",
			});
			return;
		}

		setLoading(true);

		try {
			let photoCnhPath: string | undefined;
			let isDeletePhoto = false;

			// só faz upload se tiver arquivo novo
			if (cnhFile?.file) {
				photoCnhPath = await uploadCnhFile({
					file: cnhFile.file,
					driverId: data.id,
					getPresignedUrl,
				});
			}

			// Foto removida
			if (removedCnhPhoto) {
				isDeletePhoto = true;
			}

			const payload = {
				vehicleId: values.vehicleId ? Number(values.vehicleId) : null,
				categoryCnh: Array.isArray(values.categoryCnh)
					? values.categoryCnh.join("")
					: (values.categoryCnh ?? ""),
				cnh: values.cnh?.trim() || null,
				cnhExpiration: values.cnhExpiration?.trim()
					? values.cnhExpiration
					: null,
				function: values.function ?? "",
				name: values.name.trim(),

				...(photoCnhPath !== undefined && { photoCnh: photoCnhPath }),
				...(isDeletePhoto && { isDeletePhoto: true }),
			};

			await mutateAsync({ id: data.id, payload });

			showToast({
				title: "Dados atualizados com sucesso!",
				variant: "success",
			});

			onClose();
		} catch (err) {
			handleEditDriverError(err, setError);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (!isOpen) {
			reset({
				name: "",
				cpf: "",
				email: "",
				vehicleId: "",
				function: "",
				cnh: "",
				cnhExpiration: "",
				categoryCnh: [],
				photoCnh: undefined,
			});

			setCnhFile(null);
			setRemovedCnhPhoto(false);
			setLoading(false);
		}
	}, [isOpen, reset]);

	if (!data) {
		return null;
	}

	return (
		<ModalBase
			actions={[
				{ label: "Cancelar", color: "secondary", onClick: onClose },
				{
					label: loading ? "Enviando..." : "Salvar",
					color: "primary",
					onClick: handleSubmit(onSubmit),
					disabled: loading,
				},
			]}
			isOpen={isOpen}
			onClose={onClose}
			title="Editar motorista"
			width={860}
		>
			<div className="flex flex-col gap-2.5 p-2.5">
				<div className="flex gap-2.5">
					<FormFieldControl
						error={errors.name?.message}
						htmlFor="name"
						label="Nome"
					>
						<Input
							{...register("name")}
							placeholder="Digite o nome"
							type="text"
						/>
					</FormFieldControl>

					<FormFieldControl htmlFor="cpf" label="CPF">
						<Input
							aria-label="cpf"
							autoComplete="off"
							defaultValue={formatCpfMasked(data.cpf)}
							disabled
							name="cpf"
							type="text"
						/>
					</FormFieldControl>
				</div>

				<div className="flex gap-2.5">
					<FormFieldControl htmlFor="email" label="E-mail">
						<Input
							aria-label="email"
							autoComplete="off"
							defaultValue={data.email}
							disabled
							name="email"
							type="email"
						/>
					</FormFieldControl>

					<FormFieldControl
						error={errors.vehicleId?.message}
						htmlFor="plate"
						label="Placa"
					>
						<Controller
							control={control}
							name="vehicleId"
							render={({ field }) => {
								const options = [
									{ value: null, label: "Nenhuma" },
									...(vehicles?.map((v) => ({
										value: v.id.toString(),
										label: v.plate,
									})) ?? []),
								];

								return (
									<Select
										className="w-full"
										isLoading={vehiclesLoading}
										menuPortalTarget={document.body}
										onChange={(option) => field.onChange(option?.value ?? null)}
										options={options}
										placeholder={
											vehiclesLoading
												? "Carregando placas..."
												: "Selecione a placa"
										}
										styles={{
											control: (base) => ({
												...base,
												backgroundColor: "#ffffff",
												borderColor: "#d1d5db",
											}),
											placeholder: (base) => ({
												...base,
												color: "#6b7280",
											}),
											option: (base, { isFocused, isSelected }) => ({
												...base,
												backgroundColor:
													isFocused || isSelected ? "#e5e7eb" : "#ffffff",
												color: "#111827",
											}),
											singleValue: (base) => ({
												...base,
												color: "#111827",
											}),
											menuPortal: (base) => ({ ...base, zIndex: 9999 }),
											menuList: (base) => ({
												...base,
												maxHeight: window.innerHeight < 600 ? 150 : 300,
												overflowY: "auto",
											}),
										}}
										value={options.find((opt) => opt.value === field.value)}
									/>
								);
							}}
						/>
					</FormFieldControl>
				</div>

				<div className="flex gap-2.5">
					<FormFieldControl htmlFor="function" label="Função">
						<Input
							{...register("function")}
							placeholder="Ex: Diretoria, Gerência..."
							type="text"
						/>
					</FormFieldControl>

					<FormFieldControl
						error={errors.cnh?.message}
						htmlFor="cnh"
						label="Número da CNH"
					>
						<Input
							{...register("cnh")}
							inputMode="numeric"
							maxLength={11}
							onChange={(e) => {
								const onlyNumbers = e.target.value.replace(/\D/g, "");
								e.target.value = onlyNumbers;
							}}
							placeholder="Digite o número da CNH"
							type="text"
						/>
					</FormFieldControl>
				</div>

				<div className="flex gap-2.5">
					<FormFieldControl
						error={errors.cnhExpiration?.message}
						htmlFor="cnhExpiration"
						label="Data de Vencimento da CNH"
						showIconError
					>
						<Input
							{...register("cnhExpiration")}
							max={
								new Date(new Date().setFullYear(new Date().getFullYear() + 20))
									.toISOString()
									.split("T")[0]
							}
							min={new Date("1950-01-01").toISOString().split("T")[0]}
							type="date"
						/>
					</FormFieldControl>

					<FormFieldControl
						error={errors.categoryCnh?.message}
						htmlFor="categoryCnh"
						label="Categoria da CNH"
						showIconError
					>
						<Controller
							control={control}
							name="categoryCnh"
							render={({ field }) => {
								const selectedValues: string[] = field.value || [];

								const options = ["A", "B", "C", "D", "E"].map((category) => ({
									value: category,
									label: category,
								}));

								return (
									<Select
										className="w-full"
										isMulti
										menuPortalTarget={document.body}
										onChange={(opts) =>
											field.onChange([...new Set(opts.map((o) => o.value))])
										}
										options={options}
										placeholder="Selecione as categorias"
										styles={{
											control: (base) => ({
												...base,
												backgroundColor: "#ffffff",
												borderColor: "#d1d5db",
											}),
											placeholder: (base) => ({
												...base,
												color: "#6b7280",
											}),
											option: (base, { isFocused, isSelected }) => ({
												...base,
												backgroundColor:
													isFocused || isSelected ? "#e5e7eb" : "#ffffff",
												color: "#111827",
											}),
											menuPortal: (base) => ({ ...base, zIndex: 9999 }),
											menuList: (base) => ({
												...base,
												maxHeight: window.innerHeight < 600 ? 150 : 300,
												overflowY: "auto",
											}),
										}}
										value={selectedValues.map((val) => ({
											label: val,
											value: val,
										}))}
									/>
								);
							}}
						/>
					</FormFieldControl>
				</div>

				<div className="w-full">
					<Controller
						control={control}
						name="photoCnh"
						render={({ field }) => (
							<FileInput
								error={errors.photoCnh?.message}
								initialPreview={data?.photo ?? null}
								label="Foto da CNH"
								mode="image_or_pdf"
								name="cnhPhoto"
								onChange={(file) => {
									field.onChange(file ?? undefined);
									if (file) {
										setCnhFile({ file, description: "CNH" });
										setRemovedCnhPhoto(false);
									} else {
										setCnhFile(null);
										setRemovedCnhPhoto(true);
									}
								}}
							/>
						)}
					/>
				</div>
			</div>
		</ModalBase>
	);
};

export default EditDriverModal;
